import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Check, Copy, Pause, Play, Square, Bell, ListOrdered, PenLine, Timer } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, errMsg } from "@/lib/api";
import { actMust } from "@/v2/today3/MustDos";

const path = (m, p) => `/v2/today/must/${encodeURIComponent(m.id)}/${p}`;
const input = "w-full h-11 px-3 rounded-xl border border-[#EFECE6] bg-white text-base";

function StepsTab({ m, onChange }) {
  const [steps, setSteps] = useState(m.help_steps || null);
  const [busy, setBusy] = useState(false);
  const make = async () => {
    setBusy(true);
    try { const { data } = await api.post(path(m, "help"), { mode: "steps" }); setSteps(data.steps); onChange(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const toggle = async (i) => { try { const { data } = await api.post(path(m, "steps"), { index: i }); setSteps(data.steps); onChange(); } catch (e) { toast.error(errMsg(e)); } };
  if (!steps) return <div className="space-y-3"><p className="text-sm text-[#635E59]">CAMRYN turns this into 3–5 small steps. The first one takes under 2 minutes.</p>
    <button disabled={busy} onClick={make} data-testid="help-steps-generate" className="btn-ink w-full h-12 rounded-full font-semibold disabled:opacity-50">{busy ? "Breaking it down…" : "Break it down"}</button></div>;
  return (
    <div className="space-y-2" data-testid="help-steps-list">
      {steps.map((s, i) => (
        <button key={`${i}-${s.text}`} onClick={() => toggle(i)} data-testid={`help-step-${i}`} className="w-full flex items-start gap-3 text-left rounded-xl border border-[#EFECE6] p-3 min-h-11">
          <span className={`h-6 w-6 shrink-0 rounded-md border-2 grid place-items-center ${s.done ? "bg-[#4F6B55] border-[#4F6B55] text-white" : "border-[#8C857B]"}`}>{s.done && <Check className="h-3.5 w-3.5" />}</span>
          <span className={`text-sm ${s.done ? "line-through text-[#8C857B]" : "text-[#2D2926]"}`}>{s.text}</span>
        </button>))}
      <button disabled={busy} onClick={make} data-testid="help-steps-redo" className="tap text-xs text-[#635E59] underline">{busy ? "Rewriting…" : "Break it down differently"}</button>
    </div>
  );
}

function DraftTab({ m }) {
  const [note, setNote] = useState("");
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const make = async () => {
    setBusy(true);
    try { const { data } = await api.post(path(m, "help"), { mode: "draft", note: note || null }); setDraft(data); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const copy = async () => { try { await navigator.clipboard.writeText(draft.text); toast("Copied."); } catch { toast.error("Couldn't copy. Select the text instead."); } };
  return (
    <div className="space-y-3">
      <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={300} placeholder="Who's it for, or what should it say? (optional)" data-testid="help-draft-note" className={input} />
      <button disabled={busy} onClick={make} data-testid="help-draft-generate" className="btn-ink w-full h-12 rounded-full font-semibold disabled:opacity-50">{busy ? "Drafting…" : draft ? "Draft another" : "Draft it for me"}</button>
      {draft && <div className="rounded-2xl bg-[#F5F2EC] p-4" data-testid="help-draft-result">
        <p className="text-[11px] uppercase tracking-wider font-semibold text-[#8C857B]">{draft.title}</p>
        <p className="text-sm text-[#2D2926] whitespace-pre-wrap mt-1.5 select-text">{draft.text}</p>
        <button onClick={copy} data-testid="help-draft-copy" className="mt-3 h-10 px-4 rounded-full bg-white text-sm font-semibold inline-flex items-center gap-1.5"><Copy className="h-4 w-4" />Copy</button>
      </div>}
      <p className="text-[11px] text-stone-500">Drafts aren't saved. Copy it before you close.</p>
    </div>
  );
}

function FocusTab({ m, onChange, onClose }) {
  const [mins, setMins] = useState(15);
  const [left, setLeft] = useState(null);
  const [run, setRun] = useState(false);
  const tick = useRef(null);
  useEffect(() => {
    if (!run) return undefined;
    tick.current = setInterval(() => setLeft((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(tick.current);
  }, [run]);
  useEffect(() => { if (left === 0) setRun(false); }, [left]);
  const finish = async () => { try { await actMust(m, "done"); toast.success("Done. That counts."); await onChange(); onClose(); } catch (e) { toast.error(errMsg(e)); } };
  if (left === null) return (
    <div className="space-y-3">
      <p className="text-sm text-[#635E59]">Just this one thing for a set time. Phone face down.</p>
      <div className="grid grid-cols-3 gap-2">{[10, 15, 25].map((n) => <button key={n} onClick={() => setMins(n)} data-testid={`focus-${n}`}
        className={`chip h-11 rounded-xl text-sm font-semibold border ${mins === n ? "bg-stone-900 text-white border-stone-900" : "border-stone-200"}`}>{n} min</button>)}</div>
      <button onClick={() => { setLeft(mins * 60); setRun(true); }} data-testid="focus-start" className="btn-ink w-full h-12 rounded-full font-semibold inline-flex items-center justify-center gap-2"><Play className="h-4 w-4" />Start focus</button>
    </div>
  );
  const mm = String(Math.floor(left / 60)).padStart(2, "0");
  const ss = String(left % 60).padStart(2, "0");
  return (
    <div className="text-center space-y-4" data-testid="focus-running">
      <p className="font-mono text-5xl tracking-tight text-[#2D2926]" data-testid="focus-clock">{mm}:{ss}</p>
      {left === 0 && <p className="text-sm font-semibold text-[#4F6B55]" data-testid="focus-finished">Time. Did you get it done?</p>}
      <div className="flex justify-center gap-2">
        {left > 0 && <button onClick={() => setRun(!run)} data-testid="focus-pause" className="h-11 px-4 rounded-full border border-stone-300 text-sm font-semibold inline-flex items-center gap-1.5">{run ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}{run ? "Pause" : "Resume"}</button>}
        <button onClick={() => { setRun(false); setLeft(null); }} data-testid="focus-stop" className="h-11 px-4 rounded-full border border-stone-300 text-sm font-semibold inline-flex items-center gap-1.5"><Square className="h-4 w-4" />{left === 0 ? "Not yet" : "Stop"}</button>
      </div>
      <button onClick={finish} data-testid="focus-done" className="btn-ink w-full h-12 rounded-full font-semibold">{left === 0 ? "Yes, tick it off" : "Finished early: tick it off"}</button>
    </div>
  );
}

function RemindTab({ m }) {
  const [time, setTime] = useState("");
  const [set, setSet] = useState(null);
  const save = async () => {
    try { const { data } = await api.post(path(m, "remind"), { time }); setSet(data); toast.success(`I'll nudge you at ${data.time}.`); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-3">
      <p className="text-sm text-[#635E59]">Pick a time later today and CAMRYN will send one push.</p>
      <input type="time" value={time} onChange={(e) => setTime(e.target.value)} data-testid="remind-time" className="w-40 h-11 px-3 rounded-xl border border-[#EFECE6] text-base block" />
      <button disabled={!time} onClick={save} data-testid="remind-save" className="btn-ink w-full h-12 rounded-full font-semibold disabled:opacity-40 inline-flex items-center justify-center gap-2"><Bell className="h-4 w-4" />Remind me</button>
      {set && set.devices === 0 && <p className="text-xs text-[#8A5A16] bg-[#FAF4E8] rounded-xl p-3" data-testid="remind-no-device">Saved, but this account has no device set up for pushes yet. Turn on reminders in Me to get it.</p>}
    </div>
  );
}

export function HelpSheet({ m, open, onClose, onChange }) {
  const tabs = [["steps", "Steps", ListOrdered], ["draft", "Draft", PenLine], ["focus", "Focus", Timer], ["remind", "Remind", Bell]];
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] w-[calc(100vw-1.5rem)] max-w-lg overflow-y-auto overflow-x-hidden rounded-2xl p-4 sm:p-6 [&>*]:min-w-0" data-testid="help-sheet">
        <DialogHeader>
          <span className="text-[11px] uppercase tracking-wider font-semibold text-[#C86D51]">Help me do it</span>
          <DialogTitle className="font-display text-xl leading-snug">{m.text}</DialogTitle>
          <DialogDescription className="sr-only">Break it down, draft it, focus on it or schedule a reminder.</DialogDescription>
        </DialogHeader>
        <Tabs defaultValue="steps">
          <TabsList className="grid grid-cols-4 w-full h-11">
            {tabs.map(([k, l, I]) => <TabsTrigger key={k} value={k} data-testid={`help-tab-${k}`} className="text-xs gap-1"><I className="h-3.5 w-3.5" />{l}</TabsTrigger>)}
          </TabsList>
          <TabsContent value="steps" className="pt-3"><StepsTab m={m} onChange={onChange} /></TabsContent>
          <TabsContent value="draft" className="pt-3"><DraftTab m={m} /></TabsContent>
          <TabsContent value="focus" className="pt-3"><FocusTab m={m} onChange={onChange} onClose={onClose} /></TabsContent>
          <TabsContent value="remind" className="pt-3"><RemindTab m={m} /></TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
